/**
 * Seeded PRNG (sfc32). Its state is part of the world state: it is saved, loaded and hashed,
 * so every peer draws the same numbers in the same order.
 */
export class Rng {
  readonly state = new Uint32Array(4);

  constructor(seed: number) {
    // splitmix32 expands the seed into four well-mixed words.
    let s = seed >>> 0;
    for (let i = 0; i < 4; i++) {
      s = (s + 0x9e3779b9) >>> 0;
      let z = s;
      z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
      z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
      this.state[i] = (z ^ (z >>> 16)) >>> 0;
    }
    for (let i = 0; i < 12; i++) this.nextU32();
  }

  nextU32(): number {
    const st = this.state;
    const a = st[0] as number;
    const b = st[1] as number;
    const c = st[2] as number;
    const d = st[3] as number;
    let t = (a + b) | 0;
    st[0] = b ^ (b >>> 9);
    st[1] = (c + (c << 3)) | 0;
    let c2 = (c << 21) | (c >>> 11);
    st[3] = (d + 1) | 0;
    t = (t + (st[3] as number)) | 0;
    c2 = (c2 + t) | 0;
    st[2] = c2;
    return t >>> 0;
  }

  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number {
    return Math.floor((this.nextU32() / 4294967296) * maxExclusive);
  }

  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    return min + this.int(max - min + 1);
  }

  /** True with probability numerator/denominator. Integer-only, so it stays exact. */
  chance(numerator: number, denominator: number): boolean {
    return this.int(denominator) < numerator;
  }
}
